import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import CustomToolbar from './components/CustomToolbar'
import ReactQuill, { Quill } from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import styles from './styles.module.scss'
import Icon from 'srcReact/components/icons/Icon'
import ReactDOMServer from 'react-dom/server'
import DocumentPreview from 'srcReact/MessengerPage/components/DocumentPreview/index.jsx'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import EmojiPicker from '../EmojiPicker'
import ImagePicker from 'srcReact/MessengerPage/components/ImagePicker'
import { anchorMe } from 'srcReact/helpers/utils'
import 'quill-mention'
import './MentionBlot'
import { generalService } from 'srcReact/services'
import { lightProfileActions } from '../LightProfileModal/actions'
import LightProfileProto from '../LightProfileModal/LightProfilPrototype'
import AutoLinks from 'quill-auto-links'
import { validateFiles } from 'srcReact/helpers/fileHelper'
import PlainFileNavigationPopup from '../PlainFileNavigationPopup'

Quill.register('modules/autoLinks', AutoLinks)
let Block = Quill.import('blots/block')
Block.tagName = 'DIV'
Quill.register(Block, true)
let Link = Quill.import('formats/link')
let builtInFunc = Link.sanitize

let icons = Quill.import('ui/icons')
icons['bold'] = ReactDOMServer.renderToStaticMarkup('B')
icons['italic'] = ReactDOMServer.renderToStaticMarkup('I')
icons['underline'] = ReactDOMServer.renderToStaticMarkup('U')

let handleReturnFn

const RichTextEditor = ({
  parentObjectId, // For Feeds - parent feed id for comments // For messenger - conversation id
  value = '',
  isEditMode = false,
  showTextFormatter,
  showEmojiPicker,
  showFilePicker,
  imagePickerText = '',
  showDocumentPicker = false,
  documentPickerText = '',
  showInlineToolbar = false,
  renderInlineSendButton = null,
  placeholder,
  containerCSS,
  getFileError,
  allowedImageTypes = appConstantSettings.ACCEPTED_IMAGE_TYPES,
  allowedImageSizeInMB = appConstantSettings.MAX_IMAGE_SIZE_MB,
  allowedDocumentTypes = appConstantSettings.ACCEPTED_DOCUMENT_TYPES,
  allowedDocumentSizeInMB = appConstantSettings.MAX_DOC_SIZE_MB,
  filesLimit = appConstantSettings.FILES_LIMIT,
  maxLines = appConstantSettings.MAX_LINES,
  charLimit = appConstantSettings.MAX_CHAR_COUNT,
  handleInputError,
  handleChange,
  clearEditor,
  handleFiles,
  handleReturn,
  handleEscape,
  editorId,
  editorRef,
  showToolbar = true,
  errorMessageRequired,
  errorMessageLimitExceed,
  fromMessenger = false,
  keepFormatOnPopulate = false,
  allowDocs = true,
  allowImages = true,
  focusInput = false, ////directly focus RTE(with cursor), once popup is open
  showAdvFormat = false,
  existingFiles = [],
  activeUsers = [],
  mentionMenuOrientation = 'top',
  existingEmoji = null,
  userToTag = null,
  handleFocusMode,
  sharedImageDataUrls,
  sharedImagesToUpload,
  setSharedImageDataUrls,
  setSharedImagesToUpload,
  originMessage = null,
  focusedMode //used for focus styles
}) => {
  let filesWidth = 0
  const selectedFiles = useRef([])
  const ref = useRef(editorId)
  const imageContainerRef = useRef()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    common: { isMobile },
    login: { user: loggedInUser }
  } = useSelector((state) => state)
  const [editorHtml, setEditorHtml] = useState('')
  const [imagesToUpload, setImagesToUpload] = useState({ id: 0, files: [] })
  const [imageDataUrls, setImageDataUrls] = useState({ id: 0, files: [] })
  const [isImageModalOpen, setIsImageModalOpen] = useState(false)
  const [currentFile, setCurrenFile] = useState(null)
  const urlParams = new URLSearchParams(window.location.search)
  const keyboardBinding = {
    bindings: {
      shift_enter: {
        key: 13,
        shiftKey: true,
        handler: (range, ctx) => {
          const editor = ref.current.getEditor()
          editor.insertText(range.index, '\n')
          const newIndex = range.index + 1
          editor.setSelection(newIndex, 0) // Set cursor position to the beginning of the new line
        }
      },
      enter: {
        key: 13,
        handler: (range, ctx) => {
          if (isMobile || !handleReturnFn || editorId === 'createPost') {
            const editor = ref.current.getEditor()
            editor.insertText(range.index, '\n')
            const newIndex = range.index + 1
            editor.setSelection(newIndex, 0) // Set cursor position to the beginning of the new line
          } else if (isEditMode && handleReturnFn) { // Return key bug on messenger for desktop https://app.asana.com/0/1140988629330369/1206096957488398/f
            handleReturn(isEditMode)
          } else if (handleReturnFn) {
            handleReturnFn()
          }
        }
      },
      escape: {
        key: 27,
        handler: () => {
          if (handleEscape) {
            handleEscape()
          }
        }
      },
      tab: {
        key: 9,
        handler: function () {
          return true
        }
      }
    }
  }

  const mentionBinding = {
    allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
    mentionDenotationChars: ['@'],
    defaultMenuOrientation: mentionMenuOrientation,
    isolateCharacter: true,
    blotName: 'mention',
    positioningStrategy: 'absolute',
    onSelect: function (item, insertItem) {
      insertItem(item)
    },
    source: function (searchTerm, renderList, mentionChar) {
      var getTagAttributes =
        document.getElementById(parentObjectId) ||
        document.querySelector(`.${appConstantSettings.DIV_TAG_CONTAINER}_CREATE_POST`) ||
        document.querySelector(`.${appConstantSettings.DIV_TAG_CONTAINER}_WRITE_MESSAGE`)
      let dataType = (getTagAttributes && getTagAttributes.getAttribute('data-type')) || ''
      let dataTypeId = (getTagAttributes && getTagAttributes.getAttribute('data-typeid')) || ''
      let dataGroupId = (getTagAttributes && getTagAttributes.getAttribute('data-groupid')) || ''
      if (dataType && dataTypeId) {
        // If user is a group member but not a mixr participant, then condition checks whether dataType is 'mixr' and if a dataGroupId is present
        if (dataType === 'mixr' && dataGroupId) {
          dataType = 'group' // Change dataType to 'group'
          dataTypeId = dataGroupId // Set dataTypeId to value of dataGroupId
        }
        generalService.getTagUsers(dataType, dataTypeId, searchTerm, 0, appConstantSettings.PAGINATION_QUANTITY).then(
          (response) => {
            const { users } = response
            const sortedUsers = users.sort((a, b) => {
              return a.firstname.toLowerCase().localeCompare(b.firstname.toLowerCase())
            })
            const tagUsers = sortedUsers || []
            const updatedActiveUsersInConv = []
            if (tagUsers && tagUsers.length >= 1) {
              for (let i = 0; i < tagUsers.length; i++) {
                updatedActiveUsersInConv.push(tagUsers[i])
                const value = `${tagUsers[i].firstname} ${tagUsers[i].lastname}`
                updatedActiveUsersInConv[i].value = value
              }
            }
            let values = updatedActiveUsersInConv
            if (searchTerm.length === 0) {
              renderList(values, searchTerm)
            } else {
              const matches = []
              for (let i = 0; i < values.length; i++) {
                if (~values[i].value.toLowerCase().indexOf(searchTerm.toLowerCase())) {
                  matches.push(values[i])
                }
              }
              renderList(matches, searchTerm)
            }
          },
          (error) => {
            console.log(error)
          }
        )
      } else {
        const updatedActiveUsersInConv = []
        if (activeUsers && activeUsers.length >= 1) {
          for (let i = 0; i < activeUsers.length; i++) {
            updatedActiveUsersInConv.push(activeUsers[i])
            const value = `${activeUsers[i].firstname} ${activeUsers[i].lastname}`
            updatedActiveUsersInConv[i].value = value
          }
        }
        let values = updatedActiveUsersInConv
        if (searchTerm.length === 0) {
          renderList(values, searchTerm)
        } else {
          const matches = []
          for (let i = 0; i < values.length; i++) {
            if (~values[i].value.toLowerCase().indexOf(searchTerm.toLowerCase())) {
              matches.push(values[i])
            }
          }
          renderList(matches, searchTerm)
        }
      }
    }
  }

  const modules = useMemo(
    () => ({
      ...(showToolbar
        ? {
            toolbar: {
              container: `#toolbar`
            }
          }
        : { toolbar: false }),
      ...(keepFormatOnPopulate
        ? {
            clipboard: {
              matchVisual: false
            }
          }
        : {
            clipboard: {
              matchVisual: false,
              matchers: [
                [
                  Node.ELEMENT_NODE,
                  (node, delta) => {
                    delta.ops = delta.ops.map((op) => {
                      return {
                        insert: op.insert
                      }
                    })
                    return delta
                  }
                ]
              ]
            }
          }),
      keyboard: keyboardBinding,
      mention: mentionBinding,
      autoLinks: true
    }),
    []
  )

  useEffect(() => {
    if (value && value.length > 0) {
      setEditorHtml(value)
    }
    if (editorId === 'createPost' || editorId === 'newMessage' || editorId === `commentPost${parentObjectId}`) {
      setEditorHtml(value)
    }
  }, [value])

  useEffect(() => {
    if (existingFiles.length > 0 && existingFiles.length <= filesLimit) {
      converToDataURLs(existingFiles)
    }
  }, [existingFiles])

  useEffect(() => {
    if (existingEmoji) {
      const quill = ref && ref.current.getEditor()
      if (quill) {
        const cursorPosition = quill.selection.savedRange.index
        quill.insertText(cursorPosition, existingEmoji.emoji)
        quill.setSelection(cursorPosition + 2)
      }
    }
  }, [existingEmoji])

  useEffect(() => {
    if (userToTag) {
      const user = {}
      user.value = `${userToTag.firstname} ${userToTag.lastname}`
      user.denotationChar = '@'
      user.id = userToTag.id
      const quill = ref.current.getEditor()
      const mentionModule = quill.getModule('mention')
      if (mentionModule) {
        mentionModule.insertItem(user, true)
        setTimeout(() => quill.insertText(quill.getLength() - 1, ' '), 0)
      }
    }
  }, [userToTag])

  useEffect(() => {
    if (focusInput) {
      //used setTimeout is due to ios issue
      setTimeout(() => {
        ref.current.focus()
      }, 500)
    }
  }, [focusInput])

  const handleEditorChange = (html) => {
    setEditorHtml(html)
    const text = ref && ref.current && ref.current.getEditor().getText().replace(/\n$/, '').trim()
    if (html.includes('data-denotation-char') || text?.length > 0) {
      if (!focusedMode && handleFocusMode) {
        //if there is any text in input (mobile), make it in focus mode
        handleFocusMode(true)
      }
      if (text?.length <= charLimit) {
        handleInputError && handleInputError('')
        const updatedHtmlWithLink = anchorMe(html)
        handleChange(updatedHtmlWithLink)
      } else {
        ref?.current?.getEditor()?.deleteText(charLimit, text.length)
        handleInputError && handleInputError(errorMessageLimitExceed)
      }
    } else {
      handleChange('')
      if (fromMessenger && imagesToUpload?.files?.length === 0) {
        handleInputError && handleInputError(errorMessageRequired)
      }
    }
  }

  handleReturnFn = handleReturn //Dev Note:If we don't keep handleReturn in global scope, it does not maintain the "content" state in FeedContet. i.e. on handleReturn execution, content is found as an empty string

  const appendEmoji = (emojiObject) => {
    const quill = ref && ref.current && ref.current.getEditor()
    const cursorPosition = quill.selection.savedRange.index
    quill.insertText(cursorPosition, emojiObject.emoji)
    quill.setSelection(cursorPosition + 2)
  }

  const converToDataURLs = async (files) => {
    let dataUrls = []
    let updatedFiles = []
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const data = await readFile(file)
        dataUrls.push(data)
        updatedFiles.push(file)
      }
      if (imagesToUpload?.id !== parentObjectId) {
        selectedFiles.current = [...updatedFiles]
        setImagesToUpload({ id: parentObjectId, files: [...updatedFiles] })
        setImageDataUrls({ id: parentObjectId, files: [...dataUrls] })
      } else {
        selectedFiles.current = [...(imagesToUpload?.files || []), ...updatedFiles]
        setImagesToUpload({ id: parentObjectId, files: [...(imagesToUpload?.files || []), ...updatedFiles] })
        setImageDataUrls({ id: parentObjectId, files: [...(imageDataUrls?.files || []), ...dataUrls] })
      }
      setTimeout(() => ref?.current?.focus(), 0)
    }
  }

  const uploadImageCallBack = async (files) => {
    const updatedImagesToUpload = [...(imagesToUpload?.files || []), ...files]
    if (!focusedMode && handleFocusMode) {
      //if there is any image or doc in input (mobile), make it in focus mode
      handleFocusMode(true)
    }
    if (updatedImagesToUpload.length <= filesLimit) {
      await converToDataURLs(files)
    } else {
      getFileError(t('messenger.clientErrors.maxFiles', { filesLimit }))
    }
  }

  const showImageModal = (image, imageIndex) => {
    setIsImageModalOpen(true)
    setCurrenFile(image)
  }
  const closeImageModal = () => {
    setIsImageModalOpen(false)
  }

  const readFile = useCallback((file) => {
    return new Promise((resolve, reject) => {
      try {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.readAsDataURL(file)
      } catch (error) {
        reject(error)
      }
    })
  }, [])

  const removeImage = (imageIndex) => {
    const updatedSharedImageDataUrls = sharedImageDataUrls?.filter((image, index) => imageIndex !== index)
    const updatedSharedImagesToUpload = sharedImagesToUpload?.filter((image, index) => imageIndex !== index)
    const updatedImageDataUrls = imageDataUrls?.files?.filter((imageUrl, index) => imageIndex !== index)
    const updatedImages = imagesToUpload?.files?.filter((image, index) => imageIndex !== index)
    if (setSharedImagesToUpload) {
      setSharedImageDataUrls(updatedSharedImageDataUrls)
    }
    if (setSharedImagesToUpload) {
      setSharedImagesToUpload(updatedSharedImagesToUpload)
    }
    setImageDataUrls({ id: parentObjectId, files: updatedImageDataUrls })
    setImagesToUpload({ id: parentObjectId, files: updatedImages })
    selectedFiles.current = updatedImages
    if (handleFocusMode) {
      handleFocusMode(true)
    }
  }

  const handleFocus = () => {
    if (handleFocusMode) {
      handleFocusMode(true)
    }
  }

  const handleBlur = () => {
    // if (typeof ref.current.getEditor !== 'function') return
    // if (ref.current.getEditor().getText().length - 1 === 0) {
    if (handleFocusMode) {
      handleFocusMode(false)
    }
    // }
  }
  useEffect(() => {
    if (clearEditor) {
      if (ref && ref.current) {
        ref.current.getEditor().setText('')
      }
      setImagesToUpload({ id: 0, files: [] })
      setImageDataUrls({ id: 0, files: [] })
    }
  }, [clearEditor])

  useEffect(() => {
    if (imagesToUpload?.files && handleFiles) {
      handleFiles(imagesToUpload.files)
    }
  }, [imagesToUpload?.files])

  const handlePasteFile = (e) => {
    const clipboardData = e.clipboardData || window.clipboardData
    //if there is a normal text copy paste, then do not prevent default. Prevent it only in case of files
    if (clipboardData?.files?.length > 0) {
      // && clipboardData?.items[0].kind === 'file') {
      e.preventDefault()
    }
    processPastedFiles(clipboardData?.files)
  }

  const processPastedFiles = (files) => {
    if (files?.length > 0) {
      for (let i = 0; i < files.length; i++) {
        // if (data[i].kind === 'file') {
        //   const file = data[i].getAsFile()
        //   files.push(file)
        // }
        const fileErrorMessage = validateFiles(files, true, true)
        if (fileErrorMessage) {
          switch (fileErrorMessage) {
            case 'FILE_COUNT_ERROR':
              getFileError(t('messenger.clientErrors.fileCountError', { filesLimit: filesLimit }))
              break
            case 'FILE_TYPE_ERROR':
              getFileError(t('messenger.clientErrors.fileExtError'))
              break
            case 'IMG_SIZE_ERROR':
              getFileError(
                t('messenger.clientErrors.imageFileSizeError', { imageFileSize: appConstantSettings.MAX_IMAGE_SIZE_MB })
              )
              break
            case 'DOC_SIZE_ERROR':
              getFileError(
                t('messenger.clientErrors.docFileSizeError', { docFileSize: appConstantSettings.MAX_DOC_SIZE_MB })
              )
              break
          }
        } else {
          if (getFileError) {
            getFileError('')
          }

          if (files.length > 0) {
            uploadImageCallBack([...selectedFiles.current, ...files])
          }
        }
      }
    }
  }

  useEffect(() => {
    window.addEventListener('mention-clicked', (event) => showLightModal(event), false)
    document.getElementById(editorId).addEventListener('paste', (event) => handlePasteFile(event), false)

    //if setTimeout is not set, then it will give length before text is rendered in rte
    setTimeout(() => {
      if ((isEditMode || value) && ref) {
        //put cursor at the end of content
        const quill = ref && ref.current && ref.current.getEditor()
        if (quill) {
          if (value) {
            //if we are populating existing value (e.g edit mode or from draft, then add space at the end)
            quill.insertText(quill.getLength() - 1, ' ')
          }
          const editorContentLength = quill.getLength()
          const selection = { index: editorContentLength, length: editorContentLength }
          quill.setSelection(selection)
        }
      }
    }, 0)

    return () => {
      window.removeEventListener('mention-clicked', (event) => showLightModal(event))
      window.removeEventListener('paste', (event) => handlePasteFile(event))
    }
  }, [])

  const showLightModal = (event) => {
    const { value } = event
    if (activeUsers && activeUsers.length > 0 && value && value.id > 0) {
      const user = activeUsers.find((u) => u.id == value.id)
      if (user) {
        const lp = new LightProfileProto()
        lp.userId = user.id
        dispatch(lightProfileActions.openLightProfileModal(lp))
      }
    }
  }

  return (
    <React.Fragment>
      <div className={`${styles.container} ${containerCSS}`}>
        <div className={showInlineToolbar ? styles.inlineEditorContainer : styles.withoutInlineEditorContainer}>
          <ReactQuill
            className={styles.quillContainer}
            key={editorId}
            id={editorId}
            value={editorHtml}
            ref={ref}
            theme='snow'
            onChange={handleEditorChange}
            placeholder={placeholder}
            modules={modules}
            onFocus={handleFocus}
            onBlur={handleBlur}
            formats={[
              'header',
              'font',
              'size',
              'bold',
              'italic',
              'underline',
              'strike',
              'blockquote',
              'list',
              'bullet',
              'indent',
              'link',
              'image',
              'mention'
            ]}
          />
          {renderInlineSendButton && focusedMode && renderInlineSendButton()}
        </div>
        {(showToolbar || showDocumentPicker || showInlineToolbar) && (
          <div
            className={`${styles.imageContainer} ${
              fromMessenger && focusedMode && imageDataUrls?.files?.length > 0 && styles.messengerImageContainer
            } ${fromMessenger && !focusedMode && styles.messengerImageContainerNotFocused}`}
            ref={imageContainerRef}>
            {sharedImageDataUrls?.length > 0 &&
              sharedImageDataUrls.map((imageDataUrl, index) => {
                const fileName = sharedImagesToUpload[index]?.name
                const extension = fileName?.split('.').pop().toLowerCase()

                return allowedImageTypes.includes(extension) ? (
                  <div id={`file${index}`} className={styles.imageWithCloseContainer}>
                    <div role='button' className={styles.closeButton} onClick={() => removeImage(index)}>
                      <Icon name='close24' styleModifier={styles.closeIcon} />
                    </div>
                    <button onClick={() => showImageModal(imageDataUrl)}>
                      <img className={styles.image} src={imageDataUrl} />
                    </button>
                  </div>
                ) : (
                  <div id={`file${index}`} className={styles.documentContainer}>
                    <DocumentPreview
                      fileName={fileName}
                      extension={extension?.toLowerCase()}
                      showRemove={true}
                      handleRemove={() => removeImage(index)}
                    />
                  </div>
                )
              })}
            {imageDataUrls?.files?.length > 0 &&
              imageDataUrls?.id === parentObjectId &&
              imageDataUrls?.files.map((imageDataUrl, index) => {
                const fileName = imagesToUpload.files[index]?.name
                const extension = fileName?.split('.').pop().toLowerCase()

                return allowedImageTypes.includes(extension) ? (
                  <div id={`file${index}`} className={styles.imageWithCloseContainer}>
                    <div role='button' className={styles.closeButton} onClick={() => removeImage(index)}>
                      <Icon name='close24' styleModifier={styles.closeIcon} />
                    </div>
                    <button onClick={() => showImageModal(imageDataUrl)}>
                      <img className={styles.image} src={imageDataUrl} />
                    </button>
                  </div>
                ) : (
                  <div id={`file${index}`} className={styles.documentContainer}>
                    <DocumentPreview
                      fileName={fileName}
                      extension={extension?.toLowerCase()}
                      showRemove={true}
                      handleRemove={() => removeImage(index)}
                    />
                  </div>
                )
              })}
          </div>
        )}
      </div>
      {showInlineToolbar && (
        <div className={styles.inlineToolbar}>
          <ImagePicker
            uploadFileCallback={uploadImageCallBack}
            allowedImageTypes={allowedImageTypes}
            allowedImageSizeInMB={allowedImageSizeInMB}
            allowedDocumentTypes={allowedDocumentTypes}
            allowedDocumentSizeInMB={allowedDocumentSizeInMB}
            filesLimit={filesLimit}
            getFileError={getFileError}
            allowDocs={false}
            allowImages={true}
            iconText={imagePickerText}
          />

          <ImagePicker
            uploadFileCallback={uploadImageCallBack}
            allowedDocumentTypes={allowedDocumentTypes}
            allowedDocumentSizeInMB={allowedDocumentSizeInMB}
            filesLimit={filesLimit}
            getFileError={getFileError}
            allowDocs={true}
            allowImages={false}
            iconText={documentPickerText}
          />

          <EmojiPicker containerCss={styles.emojiContainer} appendEmoji={appendEmoji} iconStyle={styles.emojiIcon} />
        </div>
      )}
      {showToolbar && (
        <CustomToolbar
          showTextFormatter={showTextFormatter}
          appendEmoji={appendEmoji}
          uploadImageCallBack={uploadImageCallBack}
          showEmojiPicker={showEmojiPicker}
          showFilePicker={showFilePicker}
          allowedImageTypes={allowedImageTypes}
          allowedImageSizeInMB={allowedImageSizeInMB}
          allowedDocumentTypes={allowedDocumentTypes}
          allowedDocumentSizeInMB={allowedDocumentSizeInMB}
          filesLimit={filesLimit}
          getFileError={getFileError}
          fromMessenger={fromMessenger}
          allowDocs={allowDocs}
          allowImages={allowImages}
          imagePickerText={imagePickerText}
          showDocumentPicker={showDocumentPicker}
          documentPickerText={documentPickerText}
          showAdvFormat={showAdvFormat}
          imageDataUrls={imageDataUrls?.files}
          imagesToUpload={imagesToUpload?.files}
          focusedMode={focusedMode || originMessage ? true : false}
        />
      )}
      {isImageModalOpen && currentFile && (
        <PlainFileNavigationPopup isOpen={isImageModalOpen} file={currentFile} closeModal={closeImageModal} />
      )}
    </React.Fragment>
  )
}

export default RichTextEditor
